import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useShopSettings from '../platform/settings/useShopSettings';

const PLANS = [
  {
    id: 'flow',
    name: 'Flow',
    price: '$79',
    tagline: 'Live board, RO management, team view.',
    features: [
      'Flow Board & Kanban',
      'RO Management',
      'Team & Technician View',
      'Scheduling (Red Book)',
    ],
  },
  {
    id: 'ops',
    name: 'Ops',
    price: '$199',
    tagline: 'Flow plus analytics, customers, and road service.',
    features: [
      'Everything in Flow',
      'Analytics & Intelligence',
      'Customer Accounts',
      'Dispatch & Road Service',
    ],
    recommended: true,
  },
  {
    id: 'full',
    name: 'Full',
    price: '$299',
    tagline: 'Complete platform — every feature, day one.',
    features: [
      'Everything in Ops',
      'Invoice & QuickBooks Export',
      'Estimate → Text Approval',
      'Customer SMS Notifications',
    ],
  },
];

const PLAN_LABELS = { flow: 'Flow', ops: 'Ops', full: 'Full' };

export default function BillingView() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const settings = useShopSettings(s => s.settings);
  const loadSettings = useShopSettings(s => s.load);

  const subscription = settings.subscription;
  const shopSettingsId = settings.business?.shopId || 'DGP-ALBANY';
  const shopName = settings.business?.shopName || '';

  const [loading, setLoading] = useState(null);
  const success = searchParams.get('success');
  const canceled = searchParams.get('canceled');

  // Reload settings after successful checkout so plan status updates
  useEffect(() => {
    if (success) {
      // Give the webhook a moment to update AppSync, then reload
      const t = setTimeout(() => loadSettings(), 3000);
      return () => clearTimeout(t);
    }
  }, [success, loadSettings]);

  const activePlan = subscription?.plan;
  const subStatus  = subscription?.status;

  const handleSubscribe = async (planId) => {
    setLoading(planId);
    try {
      const res = await fetch('/api/stripe-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan: planId, shopSettingsId, shopName }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        console.error('Checkout error:', data.error);
        setLoading(null);
      }
    } catch (err) {
      console.error('Checkout fetch failed:', err);
      setLoading(null);
    }
  };

  const handleManageBilling = async () => {
    if (!subscription?.stripeCustomerId) return;
    setLoading('portal');
    try {
      const res = await fetch('/api/stripe-portal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId: subscription.stripeCustomerId }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        console.error('Portal error:', data.error);
        setLoading(null);
      }
    } catch (err) {
      console.error('Portal fetch failed:', err);
      setLoading(null);
    }
  };

  const statusColor = {
    active: '#22c55e',
    trialing: '#3b82f6',
    past_due: '#f59e0b',
    canceled: '#ef4444',
  }[subStatus] || '#94a3b8';

  return (
    <div style={S.page}>

      <header style={S.header}>
        <button style={S.backBtn} onClick={() => navigate(-1)}>← Back</button>
        <h1 style={S.title}>Billing & Plan</h1>
        {subscription?.stripeCustomerId && (
          <button
            style={S.portalBtn}
            onClick={handleManageBilling}
            disabled={!!loading}
          >
            {loading === 'portal' ? 'Opening…' : 'Manage Billing'}
          </button>
        )}
      </header>

      {success && (
        <div style={{ ...S.flash, background: '#22c55e' }}>
          Subscription activated. Welcome to REKON.
        </div>
      )}
      {canceled && (
        <div style={{ ...S.flash, background: '#f59e0b', color: '#0f172a' }}>
          Checkout canceled. No changes were made.
        </div>
      )}

      {subStatus && (
        <div style={S.statusBar}>
          <span style={S.statusLabel}>
            Current plan: <strong>{PLAN_LABELS[activePlan] || '—'}</strong>
          </span>
          <span style={{ ...S.statusBadge, background: statusColor }}>
            {subStatus.replace('_', ' ').toUpperCase()}
          </span>
          {subscription?.currentPeriodEnd && (
            <span style={S.statusMeta}>
              Renews {new Date(subscription.currentPeriodEnd * 1000).toLocaleDateString('en-US', {
                month: 'short', day: 'numeric', year: 'numeric',
              })}
            </span>
          )}
          {subStatus === 'past_due' && (
            <span style={{ color: '#ef4444', fontSize: 13, fontWeight: 700 }}>
              Payment failed — update billing to restore access.
            </span>
          )}
        </div>
      )}

      <div style={S.grid}>
        {PLANS.map(plan => {
          const isCurrent = activePlan === plan.id && subStatus === 'active';
          const anyLoading = !!loading;
          return (
            <div
              key={plan.id}
              style={{
                ...S.card,
                ...(plan.recommended ? S.cardHighlight : {}),
                ...(isCurrent ? S.cardActive : {}),
              }}
            >
              {plan.recommended && !isCurrent && (
                <div style={S.badgeRecommended}>Most Popular</div>
              )}
              {isCurrent && (
                <div style={S.badgeCurrent}>Current Plan</div>
              )}

              <div style={S.planName}>{plan.name}</div>
              <div style={S.planPrice}>
                {plan.price}
                <span style={{ fontSize: 15, fontWeight: 500, color: '#64748b' }}>/mo</span>
              </div>
              <p style={S.planTagline}>{plan.tagline}</p>

              <ul style={S.featureList}>
                {plan.features.map(f => (
                  <li key={f} style={S.featureItem}>
                    <span style={S.check}>✓</span>
                    {f}
                  </li>
                ))}
              </ul>

              <button
                style={{
                  ...S.subscribeBtn,
                  ...(isCurrent ? S.subscribeBtnDisabled : {}),
                  ...(plan.recommended && !isCurrent ? S.subscribeBtnHighlight : {}),
                }}
                onClick={() => !isCurrent && !anyLoading && handleSubscribe(plan.id)}
                disabled={isCurrent || anyLoading}
              >
                {loading === plan.id ? 'Redirecting to Stripe…' : isCurrent ? 'Current Plan' : 'Subscribe'}
              </button>
            </div>
          );
        })}
      </div>

      <p style={S.footnote}>
        Billed monthly · Cancel anytime via Manage Billing · Secure checkout by Stripe
        {' · '}
        <button style={S.footnoteLink} onClick={() => navigate('/privacy')}>Privacy Policy</button>
      </p>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const S = {
  page: {
    minHeight: '100vh',
    background: '#f1f5f9',
    fontFamily: "'Inter','Segoe UI',sans-serif",
    color: '#0f172a',
    paddingBottom: 64,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    padding: '18px 32px',
    background: '#ffffff',
    borderBottom: '1px solid #e2e8f0',
    marginBottom: 32,
  },
  title: {
    flex: 1,
    fontSize: 20,
    fontWeight: 800,
    color: '#0f172a',
    margin: 0,
    letterSpacing: '-0.3px',
  },
  backBtn: {
    background: 'none',
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    padding: '8px 16px',
    cursor: 'pointer',
    fontSize: 13,
    color: '#64748b',
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  portalBtn: {
    background: '#0f172a',
    color: '#ffffff',
    border: 'none',
    borderRadius: 8,
    padding: '9px 18px',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 700,
    whiteSpace: 'nowrap',
  },
  flash: {
    color: '#ffffff',
    padding: '12px 32px',
    fontWeight: 700,
    fontSize: 14,
    textAlign: 'center',
    marginBottom: 8,
  },
  statusBar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 16,
    alignItems: 'center',
    maxWidth: 960,
    margin: '0 auto 28px',
    padding: '14px 24px',
    background: '#ffffff',
    borderRadius: 10,
    border: '1px solid #e2e8f0',
    boxShadow: '0 1px 3px rgba(15,23,42,0.05)',
  },
  statusLabel: { fontSize: 14, color: '#334155' },
  statusBadge: {
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: '0.6px',
    color: '#ffffff',
    padding: '3px 10px',
    borderRadius: 999,
  },
  statusMeta: { fontSize: 12, color: '#94a3b8', marginLeft: 'auto' },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 20,
    maxWidth: 960,
    margin: '0 auto',
    padding: '0 24px',
  },
  card: {
    background: '#ffffff',
    borderRadius: 12,
    border: '1px solid #e2e8f0',
    padding: '28px 24px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    position: 'relative',
    boxShadow: '0 1px 3px rgba(15,23,42,0.05)',
  },
  cardHighlight: {
    border: '2px solid #f59e0b',
    boxShadow: '0 4px 20px rgba(245,158,11,0.12)',
  },
  cardActive: {
    border: '2px solid #22c55e',
    boxShadow: '0 4px 20px rgba(34,197,94,0.10)',
  },
  badgeRecommended: {
    position: 'absolute',
    top: -13,
    left: '50%',
    transform: 'translateX(-50%)',
    background: '#f59e0b',
    color: '#0f172a',
    fontSize: 11,
    fontWeight: 800,
    padding: '3px 14px',
    borderRadius: 999,
    letterSpacing: '0.4px',
    whiteSpace: 'nowrap',
  },
  badgeCurrent: {
    position: 'absolute',
    top: -13,
    left: '50%',
    transform: 'translateX(-50%)',
    background: '#22c55e',
    color: '#ffffff',
    fontSize: 11,
    fontWeight: 800,
    padding: '3px 14px',
    borderRadius: 999,
    letterSpacing: '0.4px',
    whiteSpace: 'nowrap',
  },
  planName: { fontSize: 24, fontWeight: 900, color: '#0f172a', letterSpacing: '-0.5px' },
  planPrice: { fontSize: 40, fontWeight: 900, color: '#0f172a', lineHeight: 1, letterSpacing: '-1px' },
  planTagline: { fontSize: 13, color: '#64748b', lineHeight: 1.5, margin: 0 },
  featureList: {
    listStyle: 'none',
    padding: 0,
    margin: '4px 0',
    display: 'flex',
    flexDirection: 'column',
    gap: 9,
    flex: 1,
  },
  featureItem: { fontSize: 13, color: '#334155', display: 'flex', alignItems: 'flex-start', gap: 8 },
  check: { color: '#22c55e', fontWeight: 700, lineHeight: '19px', flexShrink: 0 },
  subscribeBtn: {
    marginTop: 8,
    padding: '13px',
    borderRadius: 8,
    background: '#0f172a',
    color: '#ffffff',
    border: 'none',
    fontSize: 14,
    fontWeight: 800,
    cursor: 'pointer',
    width: '100%',
    letterSpacing: '0.1px',
    transition: 'opacity 0.15s',
  },
  subscribeBtnDisabled: {
    background: '#e2e8f0',
    color: '#94a3b8',
    cursor: 'default',
  },
  subscribeBtnHighlight: {
    background: '#f59e0b',
    color: '#0f172a',
  },
  footnote: {
    textAlign: 'center',
    color: '#94a3b8',
    fontSize: 12,
    marginTop: 36,
  },
  footnoteLink: {
    background: 'none',
    border: 'none',
    padding: 0,
    font: 'inherit',
    color: '#94a3b8',
    textDecoration: 'underline',
    cursor: 'pointer',
  },
};
